import React, { useState } from 'react';
import { Input, Button, Snackbar } from 'geniebook-ui/dist';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { IFormPasswordData } from '@models/interfaces/user.interface';
import { useMutation } from '@tanstack/react-query';
import UserServices from '@services/GeniebookAPI/UserServices.services';
import { useViewWidth } from '@utils/helpers/viewWidth';
import { ValidationError } from 'yup';

const breakpoint = require('geniebook-ui/dist/themes/breakpoint');

const EC_PASS_SAME = '103-01';
const EC_PASS_AVOID = '103-08';
const EC_PASS_OLD = '103-02';

const ChangePasswordForm = () => {
  const navigate = useNavigate();
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [disableButton, setDisableButton] = useState(true);
  const [showSnackBar, setShowSnackBar] = useState<boolean>(false);
  const viewWidth = useViewWidth();

  const [formData, setFormData] = useState<IFormPasswordData>({
    current_password: '',
    new_password: '',
    password_confirmation: '',
  });

  const [submittedFormData, setSubmittedFormData] = useState<IFormPasswordData>(
    {
      current_password: '',
      new_password: '',
      password_confirmation: '',
    },
  );

  const [formErrors, setFormErrors] = useState<IFormPasswordData>({
    current_password: '',
    new_password: '',
    password_confirmation: '',
  });

  function handleCancel() {
    navigate('/account-settings');
  }

  // found this from https://locutus.io/php/strings/bin2hex/
  function bin2hex(input: string) {
    let i,
      l,
      n,
      o = '';

    input += '';

    for (i = 0, l = input.length; i < l; i++) {
      n = input.charCodeAt(i).toString(16);

      o += n.length < 2 ? '0' + n : n;
    }

    return o;
  }

  const validationSchema = Yup.object().shape({
    current_password: Yup.string().required('Enter your password'),
    new_password: Yup.string()
      .notOneOf(
        [Yup.ref('current_password')],
        'New password cannot be the same as the old password.',
      )
      .required('Enter your password')
      .min(8, 'Password must be minimum 8 characters.')
      .test(
        'no-leading-trailing-space',
        'Password cannot start or end with a blank space.',
        value => {
          // Use a regex pattern to check for leading or trailing spaces
          if (value) {
            return !/^\s+|\s+$/g.test(value);
          }
          return true; // If the field is empty, it's considered valid
        },
      ),
    password_confirmation: Yup.string()
      .oneOf(
        [Yup.ref('new_password')],
        'Passwords do not match. Please try again.',
      )
      .required('Enter your password')
      .min(8, 'Password must be minimum 8 characters.'),
  });

  const updatePasswordData = useMutation(
    (submittedFormData: IFormPasswordData) =>
      UserServices.updateUserProfile(submittedFormData),
    {
      onSuccess(data) {
        if (data.data.error) {
          switch (data.data.error_code) {
            case EC_PASS_SAME:
              setFormErrors({
                ...formErrors,
                new_password: data.data.error_msg,
              });
              break;
            case EC_PASS_AVOID:
              setFormErrors({
                ...formErrors,
                new_password: data.data.error_msg,
                password_confirmation: data.data.error_msg,
              });
              break;
            case EC_PASS_OLD:
              setFormErrors({
                ...formErrors,
                current_password: data.data.error_msg,
              });
              break;
            default:
              setShowSnackBar(true);
              break;
          }
        } else {
          navigate('/account-settings');
        }
      },
      onError() {
        setShowSnackBar(true);
      },
    },
  );

  const handleSave = async () => {
    // step by step
    // 1. make request to change
    // 2. invalidate react query user detail to make it re-fetch new data
    // 3. navigate to profile
    try {
      await validationSchema.validate(formData, { abortEarly: false });

      await updatePasswordData.mutateAsync(submittedFormData);
    } catch (validationErrors: unknown) {
      (validationErrors as ValidationError).inner.forEach(message => {
        setFormErrors(previousState => ({
          ...previousState,
          [message.path ?? '']: message.message,
        }));
      });
    }
  };

  return (
    <div>
      {showSnackBar ? (
        <Snackbar
          message={'Oops! Something went wrong. Please try again later.'}
          views={viewWidth < breakpoint.md ? 'mobile' : 'desktop'}
          buttonText={'Dismiss'}
          onTextClick={() => {
            setShowSnackBar(false);
          }}
          states={'error'}
          className={'fixed center-horizontal top-[60px]'}
        />
      ) : (
        <></>
      )}
      <div className={'mb-6'}>
        <div className={'relative max-w-[352px] max-md:max-w-full'}>
          <div className="absolute top-[46px] -translate-y-1/2 right-4">
            <div
              className={
                'text-primary-navyblue font-bold text-xs hover:text-primary-skyblue text-bold cursor-pointer'
              }
              onClick={() => setShowCurrent(!showCurrent)}
            >
              {showCurrent ? 'Hide' : 'Show'}
            </div>
          </div>
          <Input
            mode={'dark'}
            variant={'outside'}
            placeholder="Enter your current password"
            className="max-w-[352px] max-md:max-w-full [&>input]:!pr-16 [&>span]:!leading-4"
            label="Current Password"
            required={true}
            type={showCurrent ? 'text' : 'password'}
            value={formData.current_password}
            onChange={event => {
              setFormData({
                ...formData,
                current_password: event.target.value,
              });
              setSubmittedFormData({
                ...submittedFormData,
                current_password: btoa(bin2hex(event.target.value)),
              });
              setFormErrors({ ...formErrors, current_password: '' });
              setDisableButton(false);
            }}
            error={formErrors.current_password ?? ''}
          />
        </div>
      </div>
      <div className={'mb-6'}>
        <div className={'relative max-w-[352px] max-md:max-w-full'}>
          <div className="absolute top-[46px] -translate-y-1/2 right-4">
            <div
              className={
                'text-primary-navyblue font-bold text-xs hover:text-primary-skyblue text-bold cursor-pointer'
              }
              onClick={() => setShowNew(!showNew)}
            >
              {showNew ? 'Hide' : 'Show'}
            </div>
          </div>
          <Input
            mode={'dark'}
            variant={'outside'}
            placeholder="Enter your new password"
            className="max-w-[352px] max-md:max-w-full [&>input]:!pr-16 [&>span]:!leading-4"
            label="New Password"
            required={true}
            type={showNew ? 'text' : 'password'}
            value={formData.new_password}
            onChange={event => {
              setFormData({ ...formData, new_password: event.target.value });
              setSubmittedFormData({
                ...submittedFormData,
                new_password: btoa(bin2hex(event.target.value)),
              });
              setFormErrors({ ...formErrors, new_password: '' });
              setDisableButton(false);
            }}
            error={formErrors.new_password ?? ''}
          />
        </div>
      </div>
      <div className={'mb-10'}>
        <div className={'relative max-w-[352px] max-md:max-w-full'}>
          <div className="absolute top-[46px] -translate-y-1/2 right-4">
            <div
              className={
                'text-primary-navyblue font-bold text-xs hover:text-primary-skyblue text-bold cursor-pointer'
              }
              onClick={() => setShowConfirm(!showConfirm)}
            >
              {showConfirm ? 'Hide' : 'Show'}
            </div>
          </div>
          <Input
            mode={'dark'}
            variant={'outside'}
            placeholder="Confirm your new password"
            className="max-w-[352px] max-md:max-w-full [&>input]:!pr-16 [&>span]:!leading-4"
            label="Confirm New Password"
            required={true}
            type={showConfirm ? 'text' : 'password'}
            value={formData.password_confirmation}
            onChange={event => {
              setFormData({
                ...formData,
                password_confirmation: event.target.value,
              });
              setSubmittedFormData({
                ...submittedFormData,
                password_confirmation: btoa(bin2hex(event.target.value)),
              });
              setFormErrors({ ...formErrors, password_confirmation: '' });
              setDisableButton(false);
            }}
            error={formErrors.password_confirmation ?? ''}
          />
        </div>
      </div>
      <div className={'flex'}>
        <Button
          variant={'primary'}
          className={'font-black max-md:!w-full max-md:!max-w-full'}
          onClick={handleSave}
          disabled={disableButton}
        >
          Save
        </Button>
        <Button
          variant={'secondary'}
          className={'max-lg:hidden'}
          onClick={handleCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default ChangePasswordForm;
